
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, CheckCircle, RefreshCw, Loader2, Lock, Key } from "lucide-react";
import { Link } from "react-router-dom";

interface IntegrationCardProps {
  id: string;
  name: string;
  description: string;
  status: "idle" | "connected" | "error";
  imageUrl?: string;
  icon?: React.ReactNode;
  isLoading?: boolean;
  isTesting?: boolean;
  onTest?: () => void;
  isSecretConfigured?: boolean;
  connectionSource?: 'secrets' | 'database' | 'none';
}

export const IntegrationCard = ({ 
  id, 
  name, 
  description, 
  status, 
  imageUrl, 
  icon,
  isLoading = false,
  isTesting = false,
  onTest,
  isSecretConfigured = false,
  connectionSource = 'none'
}: IntegrationCardProps) => {
  return (
    <Card className="overflow-hidden flex flex-col">
      <CardContent className="p-6 flex-grow">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center justify-center w-16 h-16 rounded-md overflow-hidden bg-background">
            {imageUrl ? (
              <img 
                src={imageUrl} 
                alt={name} 
                className="w-12 h-12 object-contain" 
              />
            ) : icon ? (
              icon
            ) : (
              <div className="w-12 h-12 bg-muted rounded-md flex items-center justify-center text-lg font-bold text-primary">
                {name.charAt(0)}
              </div>
            )}
          </div>
          
          <Badge 
            variant={status === 'connected' ? 'success' : status === 'error' ? 'destructive' : 'outline'}
            className="ml-2"
          >
            {status === 'connected' ? 'Connected' : status === 'error' ? 'Error' : 'Not Connected'}
          </Badge>
        </div>
        
        <h3 className="font-semibold text-lg">{name}</h3>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
        
        {status === 'connected' && (
          <div className="flex items-center gap-1 mt-2 text-sm">
            {connectionSource === 'secrets' ? (
              <>
                <Key className="h-4 w-4 text-blue-600" />
                <span className="text-blue-600">Configured via Secrets</span>
              </>
            ) : connectionSource === 'database' ? (
              <>
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-green-600">Configured via Database</span>
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-green-600">Connected</span>
              </>
            )}
          </div>
        )}
      </CardContent>
      
      <CardFooter className="bg-muted/30 p-4 border-t flex items-center gap-2">
        {onTest && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onTest}
            disabled={isTesting || isLoading}
            className="flex-shrink-0"
          >
            {isTesting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-1">Test</span>
          </Button>
        )}
        
        {connectionSource === 'secrets' && (
          <Button 
            variant="outline" 
            size="sm" 
            disabled 
            className="flex-shrink-0"
          >
            <Lock className="h-3 w-3 mr-1" />
            Managed via Secrets
          </Button>
        )}

        <Button 
          variant="outline" 
          size="sm" 
          className={`${onTest ? 'ml-auto' : 'w-full'}`}
          asChild
        >
          <Link to={`/integrations?tab=${id}`}>
            Configure <ExternalLink className="ml-2 h-3 w-3" />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
};
